import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactForm  from '../components/contacts/ContactForm';
import OccasionForm from '../components/occasions/OccasionForm';
import type { Contact, Occasion } from '../../../shared/src/types/database';

// ─── Step definitions ─────────────────────────────────────────────────────────

type Step = 'who' | 'occasions' | 'more' | 'done';

const STEPS: Step[] = ['who', 'occasions', 'more', 'done'];

function stepIndex(s: Step) { return STEPS.indexOf(s); }

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ step }: { step: Step }) {
  const idx   = stepIndex(step);
  const total = STEPS.length - 1; // 'done' is not a real step
  const pct   = Math.min(((idx) / total) * 100, 100);
  return (
    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-1 bg-sage rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── Layout shell ─────────────────────────────────────────────────────────────

function Shell({ step, children }: { step: Step; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Nav */}
      <div className="px-6 py-5 flex items-center justify-between max-w-xl mx-auto w-full">
        <img
          src="/logo-wordmark.png"
          alt="MomentSent"
          className="h-10 w-auto object-contain"
        />
        {step !== 'done' && (
          <span className="font-body text-xs text-gray-400">
            Step {stepIndex(step) + 1} of {STEPS.length - 1}
          </span>
        )}
      </div>

      <ProgressBar step={step} />

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 py-10">
        <div className="w-full max-w-md">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Step: Who ────────────────────────────────────────────────────────────────

function StepWho({ onDone }: { onDone: (c: Contact, autoOccasions?: Occasion[]) => void }) {
  return (
    <>
      <p className="font-accent italic text-sage text-sm mb-2">Let's get started</p>
      <h1 className="font-heading text-3xl font-bold text-navy mb-2">
        Who's the first person you want to never forget?
      </h1>
      <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
        Think of someone whose birthday, anniversary, or special day you want to
        nail every year. We'll handle the card, you just need their name and address.
      </p>

      <ContactForm
        onSave={onDone}
        submitLabel="Save and add dates →"
      />
    </>
  );
}

// ─── Step: Occasions ─────────────────────────────────────────────────────────

function StepOccasions({
  contact,
  occasions,
  onAdd,
  onNext,
}: {
  contact:   Contact;
  occasions: Occasion[];
  onAdd:     (o: Occasion) => void;
  onNext:    () => void;
}) {
  const [adding, setAdding] = useState(occasions.length === 0);

  const occasionLabels: Record<string, string> = {
    birthday:        '🎂 Birthday',
    anniversary:     '💍 Anniversary',
    christmas:       '🎄 Christmas',
    valentines:      "💝 Valentine's Day",
    mothers_day:     "🌸 Mother's Day",
    fathers_day:     "👔 Father's Day",
    graduation:      '🎓 Graduation',
    thinking_of_you: '💌 Thinking of You',
    custom:          '✨ Custom',
  };

  return (
    <>
      <p className="font-accent italic text-sage text-sm mb-2">Step 2</p>
      <h1 className="font-heading text-3xl font-bold text-navy mb-2">
        When should we send cards?
      </h1>
      <p className="font-body text-gray-500 text-sm leading-relaxed mb-6">
        Add the dates you want MomentSent to remember for{' '}
        <span className="font-medium text-navy">{contact.name}</span>.
      </p>

      {/* Saved occasions */}
      {occasions.length > 0 && (
        <ul className="flex flex-col gap-2 mb-4">
          {occasions.map((o) => (
            <li
              key={o.id}
              className="flex items-center gap-3 rounded-xl bg-white shadow-warm px-4 py-3"
            >
              <span className="text-sm font-body text-gray-700">
                {occasionLabels[o.occasion_type] ?? o.occasion_type}
              </span>
              <span className="text-xs text-gray-400 ml-auto">
                {o.occasion_date}
              </span>
              <svg className="h-4 w-4 text-sage flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </li>
          ))}
        </ul>
      )}

      {/* Add form */}
      {adding ? (
        <div className="rounded-[16px] bg-white shadow-warm p-5 mb-4">
          <OccasionForm
            contactId={contact.id}
            contactName={contact.name}
            onSave={(o) => { onAdd(o); setAdding(false); }}
            onCancel={occasions.length > 0 ? () => setAdding(false) : undefined}
            submitLabel="Add date →"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full rounded-xl border-2 border-dashed border-sage/30 py-3 text-sm font-body text-sage hover:border-sage/60 hover:bg-sage/5 transition-all mb-4"
        >
          + Add another date
        </button>
      )}

      {/* Next */}
      {occasions.length > 0 && !adding && (
        <button
          type="button"
          onClick={onNext}
          className="btn-primary w-full mt-2"
        >
          Continue →
        </button>
      )}

      {occasions.length === 0 && !adding && (
        <button
          type="button"
          onClick={onNext}
          className="btn-secondary w-full mt-2 text-sm"
        >
          Skip for now
        </button>
      )}
    </>
  );
}

// ─── Step: Add more ───────────────────────────────────────────────────────────

function StepMore({
  onAddAnother,
  onFinish,
}: {
  onAddAnother: () => void;
  onFinish:     () => void;
}) {
  return (
    <>
      <p className="font-accent italic text-sage text-sm mb-2">Step 3</p>
      <h1 className="font-heading text-3xl font-bold text-navy mb-3">
        Anyone else worth remembering?
      </h1>
      <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
        Think parents, siblings, close friends, anyone whose day you want to
        nail every year without thinking about it. Add them now or from your
        dashboard anytime.
      </p>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onAddAnother}
          className="btn-primary w-full"
        >
          + Add another person
        </button>
        <button
          type="button"
          onClick={onFinish}
          className="btn-secondary w-full"
        >
          I'm good for now →
        </button>
      </div>
    </>
  );
}

// ─── Step: Done ───────────────────────────────────────────────────────────────

function StepDone({ count }: { count: number }) {
  const navigate = useNavigate();
  return (
    <div className="text-center">
      <div className="text-5xl mb-5">💌</div>
      <h1 className="font-heading text-3xl font-bold text-navy mb-3">
        You're all set!
      </h1>
      <p className="font-body text-gray-500 text-sm leading-relaxed mb-2">
        {count === 1
          ? "We've got one person's dates locked in."
          : `We've got dates locked in for ${count} people.`}
      </p>
      <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
        We'll send you a heads-up 14 days before each occasion, enough time
        to review the message and make it yours. Then we handle printing,
        postage, and delivery. You just take the credit.
      </p>
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className="btn-primary px-10"
      >
        Go to my dashboard →
      </button>
    </div>
  );
}

// ─── OnboardingPage ───────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const [step,      setStep     ] = useState<Step>('who');
  const [contacts,  setContacts ] = useState<Contact[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  // Track which contact we're currently adding occasions for
  const [activeIdx, setActiveIdx] = useState(0);

  const currentContact = contacts[activeIdx] ?? null;
  const currentContactOccasions = occasions.filter(
    (o) => o.contact_id === currentContact?.id,
  );

  function handleContactSaved(c: Contact, autoOccasions?: Occasion[]) {
    setContacts((prev) => [...prev, c]);
    setActiveIdx(contacts.length);
    if (autoOccasions && autoOccasions.length > 0) {
      setOccasions((prev) => [...prev, ...autoOccasions]);
    }
    setStep('occasions');
  }

  function handleOccasionAdded(o: Occasion) {
    setOccasions((prev) => [...prev, o]);
  }

  function handleAddAnother() {
    setStep('who');
  }

  function handleFinish() {
    setStep('done');
  }

  return (
    <Shell step={step}>
      {step === 'who' && (
        <StepWho onDone={handleContactSaved} />
      )}

      {step === 'occasions' && currentContact && (
        <StepOccasions
          contact={currentContact}
          occasions={currentContactOccasions}
          onAdd={handleOccasionAdded}
          onNext={() => setStep('more')}
        />
      )}

      {step === 'more' && (
        <StepMore
          onAddAnother={handleAddAnother}
          onFinish={handleFinish}
        />
      )}

      {step === 'done' && (
        <StepDone count={contacts.length} />
      )}
    </Shell>
  );
}
